import type { JsonObject, JsonValue } from '@protobuf-ts/runtime';
import JSON5 from 'json5';
import { isPlainObject } from 'lodash-es';
import type { ReadonlyDeep, ValueOf } from 'type-fest';

export const ConfigErrorTypes = {
  SEMANTIC: 'semantic',
  SYNTAX: 'syntax',
} as const;

export type ConfigErrorType = ValueOf<typeof ConfigErrorTypes>;

export enum SemanticErrorLevel {
  ERROR = 'error',
  WARNING = 'warning',
}

export interface ConfigSyntaxError {
  type: typeof ConfigErrorTypes.SYNTAX;
  message: string;
}

export type ReadonlyConfigErrors = ReadonlyDeep<ConfigErrors>;
export type ReadonlyConfigSemanticError = ReadonlyDeep<ConfigSemanticError>;

type ConfigErrors = (ConfigSemanticError | ConfigSyntaxError)[];

export interface ConfigSemanticError {
  type: typeof ConfigErrorTypes.SEMANTIC;
  path: (string | number)[];
  message: string;
  level: SemanticErrorLevel;
  disablesBuilder: boolean;
  resourceIDs?: Set<string>;
}

export type JSONParseResult = JSONParseSuccess | JSONParseError;

export interface JSONParseSuccess {
  success: true;
  data: JsonObject;
}

export interface JSONParseError {
  success: false;
  errorType: typeof ConfigErrorTypes.SYNTAX;
  error: string;
  data: string;
}

/** Parse a JSON object, returning an error if invalid. */
export const safeParseJSONObject = (value: string): JSONParseResult => {
  try {
    const data = JSON.parse(value) as JsonValue;

    if (isPlainObject(data)) {
      return { success: true, data: data as JsonObject };
    }
  } catch (error) {
    // Attempt to fix common JSON issues
    const fixed = tryFixJSON(value);
    if (fixed !== undefined) {
      const fixedData = JSON.parse(fixed) as JsonValue;
      if (isPlainObject(fixedData)) {
        return { success: true, data: fixedData as JsonObject };
      }
    }

    return {
      success: false,
      errorType: ConfigErrorTypes.SYNTAX,
      error:
        error instanceof SyntaxError ? error.message : 'Error parsing JSON',
      data: value,
    };
  }

  return {
    success: false,
    errorType: ConfigErrorTypes.SYNTAX,
    error: 'Value must be an object',
    data: value,
  };
};

export const configItemToString = <T>(
  item: T,
  toRawItem?: (item: T) => unknown
): string => JSON.stringify(toRawItem ? toRawItem(item) : item, undefined, 2);

export const tryFixJSON = (input: string): string | undefined => {
  try {
    JSON.parse(input);
    return input;
  } catch {
    // Standard JSON failed - try JSON5
    try {
      return JSON.stringify(JSON5.parse(input), undefined, 2);
    } catch {
      return undefined;
    }
  }
};
